import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import PortfolioModal from '../components/PortfolioModal';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { fmtDate, cn } from '../lib/utils';
import {
  eachDayOfInterval, subDays, format, isSameDay,
  getDay, parseISO, startOfDay, isBefore, isAfter,
} from 'date-fns';

const anim = {
  container: { show: { transition: { staggerChildren: 0.1 } } },
  item: { hidden: { opacity: 0, y: 15 }, show: { opacity: 1, y: 0 } },
};

const DAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const ATT_TYPE_STYLES = {
  regular:  { label: 'Regular',  variant: 'default' },
  makeup:   { label: 'Makeup',   variant: 'primary' },
  extra:    { label: 'Extra',    variant: 'warning' },
  absent:   { label: 'Absent',   variant: 'danger'  },
  no_class: { label: 'No Class', variant: 'danger'  },
};

const STATUS_STYLES = {
  present: { label: 'Present', variant: 'success' },
  absent:  { label: 'Absent',  variant: 'danger'  },
};

const FEED_LIMIT_OPTIONS = [3, 5, 10, 15, 30, 50];

const StatCard = ({ icon, label, value, color, bg }) => (
  <motion.div
    variants={anim.item}
    whileHover={{ y: -3 }}
    className="bg-white dark:bg-slate-900 p-5 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800"
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${bg} ${color}`}>
      <SafeIcon icon={icon} size={20} />
    </div>
    <p className="text-[10px] font-medium text-slate-400 mb-0.5">{label}</p>
    <h3 className="text-2xl font-bold dark:text-white">{value}</h3>
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile: currentProfile, isAdmin } = useRole();
  const [stats, setStats] = useState({
    totalStudents: 0, totalSubjects: 0,
    todayAttendance: 0, pendingInvoices: 0,
  });
  const [recent, setRecent] = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attFilter, setAttFilter] = useState('feed');
  const [feedLimit, setFeedLimit] = useState(5);

  const [edit, setEdit] = useState({
    open: false, record: null,
    form: { date: '', time: '', subject_id: '', status: 'present', attendance_type: 'regular', makeup_for_date: '' },
    subjects: [],
  });
  const [portfolio, setPortfolio] = useState({ open: false, record: null, data: null });
  const [resolveModal, setResolveModal] = useState({
    open: false, item: null,
    form: { status: 'present', type: 'regular', time: '14:00', makeup_for: '' },
  });

  // RLS automatically filters data to only the teacher's assigned students
  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const lookbackDate = subDays(new Date(), 60).toISOString().split('T')[0];

      const [s, sub, att, inv, enrollRes, attAllRes] = await Promise.all([
        supabase.from('students').select('*', { count: 'exact', head: true }),
        supabase.from('subjects').select('*', { count: 'exact', head: true }),
        supabase
          .from('attendance')
          .select('*,students(full_name,id),subjects(name),student_portfolios(id)')
          .order('timestamp', { ascending: false })
          .limit(50),
        supabase.from('invoices').select('*', { count: 'exact', head: true }).eq('status', 'unpaid'),
        supabase
          .from('student_subjects')
          .select('*,students(id,full_name),subjects(id,name,level)'),
        supabase
          .from('attendance')
          .select('student_id,subject_id,timestamp,makeup_for_date,attendance_type')
          .gte('timestamp', `${lookbackDate}T00:00:00`),
      ]);

      setStats({
        totalStudents: s.count || 0,
        totalSubjects: sub.count || 0,
        todayAttendance: att.data?.filter(a => a.timestamp.startsWith(today)).length || 0,
        pendingInvoices: inv.count || 0,
      });
      setRecent(att.data || []);
      setAllEnrollments(enrollRes.data || []);
      setAllAttendance(attAllRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const actionRequired = useMemo(() => {
    if (!allEnrollments.length) return [];
    const items = [];
    const today = startOfDay(new Date());
    const lookbackStart = subDays(today, 60);

    for (const en of allEnrollments) {
      const scheduledDays = en.scheduled_days || [];
      if (!scheduledDays.length) continue;
      const scheduledDayNums = scheduledDays.map(d => DAY_MAP[d]).filter(n => n !== undefined);

      const enrollStart = en.enrollment_start_date ? startOfDay(parseISO(en.enrollment_start_date)) : null;
      const enrollEnd   = en.enrollment_end_date   ? startOfDay(parseISO(en.enrollment_end_date))   : null;

      const effectiveStart = enrollStart && isAfter(enrollStart, lookbackStart) ? enrollStart : lookbackStart;
      if (enrollStart && !isBefore(enrollStart, today)) continue;
      if (enrollEnd && isBefore(enrollEnd, lookbackStart)) continue;

      const yesterday    = subDays(today, 1);
      const effectiveEnd = enrollEnd && isBefore(enrollEnd, yesterday) ? enrollEnd : yesterday;
      if (isAfter(effectiveStart, effectiveEnd)) continue;

      const lookback = eachDayOfInterval({ start: effectiveStart, end: effectiveEnd });
      for (const date of lookback) {
        const dayNum  = getDay(date);
        if (!scheduledDayNums.includes(dayNum)) continue;
        const dateStr = format(date, 'yyyy-MM-dd');
        const hasRecord = allAttendance.some(
          a => a.student_id === en.student_id && a.subject_id === en.subject_id && isSameDay(new Date(a.timestamp), date)
        );
        const hasMakeup = allAttendance.some(
          a => a.student_id === en.student_id && a.subject_id === en.subject_id && a.makeup_for_date === dateStr
        );
        if (!hasRecord && !hasMakeup) {
          items.push({
            date, dateStr,
            student_id:    en.student_id,
            student_name:  en.students?.full_name,
            subject_id:    en.subject_id,
            subject_name:  en.subjects?.name,
            subject_level: en.subjects?.level,
            enrollment:    en,
          });
        }
      }
    }

    const seen = new Set();
    return items
      .filter(item => {
        const key = `${item.student_id}-${item.subject_id}-${item.dateStr}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => b.date - a.date);
  }, [allEnrollments, allAttendance]);

  const openPortfolio = (record) => {
    setPortfolio({ open: true, record, data: null });
  };

  const openEdit = async (r) => {
    const d = new Date(r.timestamp);
    const { data } = await supabase
      .from('student_subjects')
      .select('subject_id,subjects(id,name,level)')
      .eq('student_id', r.student_id);
    setEdit({
      open: true, record: r, subjects: data || [],
      form: {
        date:             fmtDate(d, 'yyyy-MM-dd'),
        time:             fmtDate(d, 'HH:mm'),
        subject_id:       r.subject_id,
        status:           r.status || 'present',
        attendance_type:  r.attendance_type || 'regular',
        makeup_for_date:  r.makeup_for_date || '',
      },
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const l = toast.loading('Updating...');
    try {
      const ts = new Date(`${edit.form.date}T${edit.form.time}`).toISOString();
      await supabase.from('attendance').update({
        subject_id:      edit.form.subject_id,
        timestamp:       ts,
        status:          edit.form.status,
        attendance_type: edit.form.attendance_type,
        makeup_for_date: edit.form.attendance_type === 'makeup' ? edit.form.makeup_for_date || null : null,
      }).eq('id', edit.record.id);
      toast.success('Updated', { id: l });
      setEdit({ ...edit, open: false });
      fetchData();
    } catch (err) {
      toast.error(err.message, { id: l });
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this attendance record?')) return;
    const l = toast.loading('Deleting...');
    await supabase.from('attendance').delete().eq('id', edit.record.id);
    toast.success('Deleted', { id: l });
    setEdit({ ...edit, open: false });
    fetchData();
  };

  const openResolve = (item) => {
    setResolveModal({ open: true, item, form: { status: 'present', type: 'regular', time: '14:00', makeup_for: '' } });
  };

  const submitResolve = async (e) => {
    e.preventDefault();
    const l = toast.loading('Saving...');
    try {
      const timestamp = new Date(`${resolveModal.item.dateStr}T${resolveModal.form.time}`).toISOString();
      await supabase.from('attendance').insert([{
        student_id:      resolveModal.item.student_id,
        subject_id:      resolveModal.item.subject_id,
        timestamp,
        status:          resolveModal.form.status,
        attendance_type: resolveModal.form.type,
        makeup_for_date: resolveModal.form.type === 'makeup' ? resolveModal.form.makeup_for || null : null,
        duration_hours:  resolveModal.item.enrollment.custom_duration ?? resolveModal.item.enrollment.subjects?.duration_hours ?? 1,
      }]);
      toast.success('Resolved!', { id: l });
      setResolveModal({ ...resolveModal, open: false });
      fetchData();
    } catch (err) {
      toast.error(err.message, { id: l });
    }
  };

  // Returns true if the current user may edit/portfolio this feed record.
  // Admins always can. Teachers only if they are main or relief teacher for
  // the subject/student combination of this record.
  const canInteractWithRecord = (r) => {
    if (isAdmin) return true;
    const enrollment = allEnrollments.find(
      en => en.student_id === r.student_id && en.subject_id === r.subject_id
    );
    if (!enrollment) return false;
    return (
      enrollment.main_teacher_id === currentProfile?.id ||
      (enrollment.relief_teacher_ids || []).includes(currentProfile?.id)
    );
  };

  const filteredRecent = recent.filter(r => {
    if (attFilter === 'feed')    return true;
    if (attFilter === 'present') return r.status === 'present';
    if (attFilter === 'absent')  return r.status === 'absent';
    if (attFilter === 'makeup')  return r.attendance_type === 'makeup';
    return true;
  });

  const displayedRecent = feedLimit === 'all'
    ? filteredRecent
    : filteredRecent.slice(0, feedLimit);

  if (loading) return (
    <div className="p-4 space-y-6">
      <Skeleton className="h-12 w-1/4" />
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    </div>
  );

  return (
    <motion.div variants={anim.container} initial="hidden" animate="show" className="space-y-6 pb-10 px-4">
      <motion.div variants={anim.item} className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold dark:text-white">Dashboard</h1>
          <p className="text-slate-500 font-medium text-sm">Center performance overview</p>
        </div>
        <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-xs font-semibold text-slate-600">Live</span>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="My Students"      value={stats.totalStudents}    icon={Lu.LuUsers}       color="text-blue-600"    bg="bg-blue-50/50" />
        <StatCard label="Active Subjects"  value={stats.totalSubjects}    icon={Lu.LuBookOpen}    color="text-indigo-600"  bg="bg-indigo-50/50" />
        <StatCard label="Today's Presence" value={stats.todayAttendance}  icon={Lu.LuCheckCircle} color="text-emerald-600" bg="bg-emerald-50/50" />
        <StatCard label="Unpaid Invoices"  value={stats.pendingInvoices}  icon={Lu.LuAlertCircle} color="text-rose-600"    bg="bg-rose-50/50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attendance Feed */}
        <motion.div variants={anim.item} className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="p-5 border-b border-slate-50 dark:border-slate-800">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold dark:text-white flex items-center gap-2">
                <SafeIcon icon={Lu.LuClock} className="text-primary-600" size={16} />
                Attendance Feed
              </h2>
              <button onClick={() => navigate('/scanner')} className="text-xs font-bold text-primary-600 hover:underline">Scanner</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'feed',    label: 'All' },
                { key: 'present', label: 'Present' },
                { key: 'absent',  label: 'Absent' },
                { key: 'makeup',  label: 'Makeup' },
                { key: 'action',  label: 'Action', count: actionRequired.length, alert: actionRequired.length > 0 },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setAttFilter(f.key)}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-lg text-[9px] font-bold border transition-all',
                    attFilter === f.key
                      ? f.alert ? 'bg-rose-600 text-white border-rose-600' : 'bg-primary-600 text-white border-primary-600'
                      : f.alert ? 'bg-rose-50 text-rose-500 border-rose-100' : 'bg-slate-50 text-slate-400 border-slate-100'
                  )}
                >
                  {f.alert && attFilter !== 'action' && <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse" />}
                  {f.label}
                  {f.count !== undefined && (
                    <span className={cn('px-1 py-0.5 rounded-full text-[8px] font-bold', attFilter === f.key ? 'bg-white/20' : 'bg-rose-100 text-rose-600')}>
                      {f.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {attFilter !== 'action' && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-50 dark:border-slate-800">
                <span className="text-[10px] font-medium text-slate-400">Show</span>
                <select
                  value={feedLimit === 'all' ? 'all' : String(feedLimit)}
                  onChange={e => setFeedLimit(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="text-xs font-medium border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-slate-50 dark:bg-slate-800 outline-none text-slate-600 dark:text-slate-300 cursor-pointer"
                >
                  {FEED_LIMIT_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                  <option value="all">All</option>
                </select>
                <span className="text-[10px] text-slate-400 font-medium">of {filteredRecent.length} records</span>
              </div>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {attFilter === 'action' && (
              <div>
                {actionRequired.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <SafeIcon icon={Lu.LuCheckCircle} size={24} className="text-emerald-500" />
                    </div>
                    <p className="font-semibold text-xs text-slate-400">All Clear!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    <div className="px-5 py-2 bg-rose-50 border-b border-rose-100 flex items-center gap-2">
                      <SafeIcon icon={Lu.LuAlertTriangle} size={12} className="text-rose-500" />
                      <p className="text-[9px] font-bold text-rose-600">{actionRequired.length} unresolved sessions</p>
                    </div>
                    {actionRequired.map((item, i) => (
                      <motion.div
                        key={`${item.student_id}-${item.subject_id}-${item.dateStr}`}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.02 * i }}
                        className="px-5 py-3 flex items-center gap-3 hover:bg-rose-50/30"
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-rose-50 text-rose-500 shrink-0">
                          <SafeIcon icon={Lu.LuAlertTriangle} size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <button onClick={() => navigate(`/students/${item.student_id}`)} className="font-bold text-xs dark:text-white hover:text-primary-600">
                            {item.student_name}
                          </button>
                          <p className="text-[9px] text-slate-400 font-medium">
                            {item.subject_name} • {fmtDate(item.date, 'dd MMM yyyy')}
                          </p>
                        </div>
                        <button
                          onClick={() => openResolve(item)}
                          className="px-3 py-1.5 bg-slate-900 text-white text-[9px] font-bold rounded-lg hover:bg-primary-600 transition-colors shrink-0"
                        >
                          Resolve
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {attFilter !== 'action' && (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredRecent.length === 0 && (
                  <div className="p-8 text-center text-slate-400 text-xs font-medium">No records found</div>
                )}
                {displayedRecent.map((r, i) => {
                  const statusStyle = STATUS_STYLES[r.status] || STATUS_STYLES.absent;
                  const typeStyle   = ATT_TYPE_STYLES[r.attendance_type] || ATT_TYPE_STYLES.regular;
                  return (
                    <motion.div
                      key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.03 * i }}
                      className="px-5 py-3 flex items-center gap-3"
                    >
                      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center border shrink-0', r.status === 'present' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600')}>
                        <SafeIcon icon={r.status === 'present' ? Lu.LuCheckCircle : Lu.LuXCircle} size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 mb-0.5">
                          <button onClick={() => navigate(`/students/${r.students?.id}`)} className="font-bold text-xs dark:text-white hover:text-primary-600">
                            {r.students?.full_name}
                          </button>
                          <Badge variant={statusStyle.variant} className="text-[7px] uppercase font-black px-1.5 py-0.5">{statusStyle.label}</Badge>
                          <Badge variant={typeStyle.variant}   className="text-[7px] uppercase font-black px-1.5 py-0.5">{typeStyle.label}</Badge>
                        </div>
                        <p className="text-[9px] text-slate-400 font-medium">
                          {r.subjects?.name} • {fmtDate(r.timestamp, 'dd MMM')} • {fmtDate(r.timestamp, 'h:mm a')}
                        </p>
                      </div>
                      {(() => {
                        const canAct = canInteractWithRecord(r);
                        return (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={canAct ? () => openPortfolio(r) : undefined}
                              disabled={!canAct}
                              title={canAct ? 'Lesson Portfolio' : 'Not your subject'}
                              className={cn(
                                'p-1.5 rounded-lg transition-colors',
                                canAct
                                  ? r.student_portfolios?.length > 0
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                    : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                                  : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-50'
                              )}
                            >
                              <SafeIcon icon={Lu.LuClipboardList} size={13} />
                            </button>
                            <button
                              onClick={canAct ? () => openEdit(r) : undefined}
                              disabled={!canAct}
                              title={canAct ? 'Edit record' : 'Not your subject'}
                              className={cn(
                                'p-1.5 rounded-lg transition-colors',
                                canAct
                                  ? 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                                  : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-50'
                              )}
                            >
                              <SafeIcon icon={Lu.LuPencil} size={13} />
                            </button>
                          </div>
                        );
                      })()}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* Sidebar */}
        <motion.div variants={anim.item} className="bg-slate-900 p-6 rounded-xl text-white relative overflow-hidden">
          <SafeIcon icon={Lu.LuTrendingUp} size={80} className="absolute top-0 right-0 p-6 opacity-10" />
          {actionRequired.length > 0 && (
            <div className="bg-rose-500/20 border border-rose-400/30 rounded-xl p-4 mb-5">
              <div className="flex items-center gap-2 mb-1">
                <SafeIcon icon={Lu.LuAlertTriangle} size={14} className="text-rose-400" />
                <p className="text-[9px] font-bold text-rose-300">Action Required</p>
              </div>
              <p className="text-2xl font-bold text-white">{actionRequired.length}</p>
              <p className="text-[9px] text-rose-300 font-medium mt-0.5">Unresolved sessions</p>
              <button
                onClick={() => setAttFilter('action')}
                className="mt-2 w-full py-1.5 bg-rose-500 hover:bg-rose-400 text-white text-[9px] font-bold rounded-lg transition-colors"
              >
                Review Now
              </button>
            </div>
          )}
          <h3 className="text-base font-bold mb-2">Performance Pulse</h3>
          <p className="text-slate-400 mb-5 text-xs leading-relaxed">Maintain consistent tracking for billing accuracy.</p>
          <Button onClick={() => navigate('/analytics')} variant="secondary" className="w-full bg-white text-slate-900 py-3 rounded-xl font-bold text-sm">
            View Analytics
          </Button>
        </motion.div>
      </div>

      {portfolio.open && (
        <PortfolioModal
          isOpen={portfolio.open}
          onClose={() => setPortfolio({ ...portfolio, open: false })}
          attendanceRecord={portfolio.record}
          onSave={fetchData}
        />
      )}

      <Modal isOpen={edit.open} onClose={() => setEdit({ ...edit, open: false })} title="Edit Attendance Record" className="max-w-md">
        {edit.record && (
          <form onSubmit={handleEdit} className="p-5 space-y-5">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
              <p className="text-[9px] font-medium text-slate-400 mb-1">Editing Session</p>
              <p className="font-bold text-sm">{edit.record.students?.full_name}</p>
              <p className="text-[9px] text-slate-400 font-medium mt-0.5">{fmtDate(edit.record.timestamp, 'EEEE, dd MMM yyyy')}</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 px-1 block">Subject</label>
              <select
                className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none focus:ring-2 focus:ring-primary-500"
                value={edit.form.subject_id}
                onChange={e => setEdit({ ...edit, form: { ...edit.form, subject_id: e.target.value } })}
              >
                {edit.subjects.map(ss => <option key={ss.subject_id} value={ss.subject_id}>{ss.subjects?.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Presence</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none" value={edit.form.status} onChange={e => setEdit({ ...edit, form: { ...edit.form, status: e.target.value } })}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Type</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none" value={edit.form.attendance_type} onChange={e => setEdit({ ...edit, form: { ...edit.form, attendance_type: e.target.value } })}>
                  <option value="regular">Regular</option>
                  <option value="makeup">Makeup</option>
                  <option value="extra">Extra</option>
                  <option value="absent">Absent</option>
                  <option value="no_class">No Class</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input type="date" label="Date" value={edit.form.date} onChange={e => setEdit({ ...edit, form: { ...edit.form, date: e.target.value } })} required />
              <Input type="time" label="Time" value={edit.form.time} onChange={e => setEdit({ ...edit, form: { ...edit.form, time: e.target.value } })} required />
            </div>
            {edit.form.attendance_type === 'makeup' && (
              <Input type="date" label="Makeup For" value={edit.form.makeup_for_date} onChange={e => setEdit({ ...edit, form: { ...edit.form, makeup_for_date: e.target.value } })} />
            )}
            <div className="flex gap-3">
              <Button variant="danger" type="button" onClick={handleDelete} className="px-4"><SafeIcon icon={Lu.LuTrash2} /></Button>
              <Button type="button" variant="ghost" onClick={() => setEdit({ ...edit, open: false })} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-[2] font-bold text-[10px]">Save</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={resolveModal.open} onClose={() => setResolveModal({ ...resolveModal, open: false })} title="Resolve Missed Session" className="max-w-md">
        {resolveModal.item && (
          <form onSubmit={submitResolve} className="p-5 space-y-5">
            <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl">
              <p className="font-bold text-sm dark:text-slate-900">{resolveModal.item.student_name}</p>
              <p className="text-[9px] text-slate-500 font-medium mt-1">{resolveModal.item.subject_name} • {fmtDate(resolveModal.item.date, 'EEEE, dd MMM yyyy')}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Presence</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none" value={resolveModal.form.status} onChange={e => setResolveModal(p => ({ ...p, form: { ...p.form, status: e.target.value } }))}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 px-1 block">Type</label>
                <select className="w-full bg-slate-50 p-3 rounded-xl font-bold text-xs border outline-none" value={resolveModal.form.type} onChange={e => setResolveModal(p => ({ ...p, form: { ...p.form, type: e.target.value } }))}>
                  <option value="regular">Regular</option>
                  <option value="absent">Absent</option>
                  <option value="no_class">No Class</option>
                  <option value="makeup">Makeup</option>
                </select>
              </div>
            </div>
            <Input type="time" label="Session Time" value={resolveModal.form.time} onChange={e => setResolveModal(p => ({ ...p, form: { ...p.form, time: e.target.value } }))} required />
            {resolveModal.form.type === 'makeup' && (
              <Input type="date" label="Makeup For" value={resolveModal.form.makeup_for} onChange={e => setResolveModal(p => ({ ...p, form: { ...p.form, makeup_for: e.target.value } }))} />
            )}
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={() => setResolveModal({ ...resolveModal, open: false })} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-[2] font-bold text-[10px]">Confirm & Resolve</Button>
            </div>
          </form>
        )}
      </Modal>
    </motion.div>
  );
}